/**
 * Доля хвоста публичного рендерера: вид `cookie_consent` (баннер согласия на
 * cookie).
 *
 * WEB-2920, заход 3: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с карточкой баннера (`CookieConsentCard`) и обёрткой с порталом
 * (`CookieConsentBlock`, свои хуки — это компоненты, а не ветка `switch`).
 * Метка `case 'cookie_consent'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderCookieConsent(ctx)`, замыкания фасада приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
// WEB-1043: один источник переноса оформления на порталы для ОБОИХ
// потребителей — попапа даунселла (WEB-712) и cookie-баннера.
import { usePortalSurfaceVars } from '../usePortalSurfaceVars';
import { safeHref } from '@/utils/isAllowedUrl';
// WEB-1330 п.2 (вариант А): cookie_consent — не декорация, кнопки пишут
// решение через тот же арбитр согласия, что и системный ConsentBanner.
import {
  readConsentDecision,
  writeConsentDecision,
} from '@/utils/analytics/euConsent';
import { useConsentActions } from '../ConsentActionsContext';
import { blockTextRoles } from '../semanticTextRoles';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { aiFields } from '../aiShapedFields';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

interface CookieConsentBlockProps {
  /** WEB-672 2a: pre-rendered rich/plain text node. */
  text: React.ReactNode;
  acceptLabel: string;
  declineLabel: string;
  policyUrl?: string;
  position: 'bottom' | 'modal';
  /** Подпись ссылки на политику — из словаря по языку воронки (репорт 01.09). */
  learnMoreLabel: string;
  /**
   * WEB-1692 юнит 7: накладка роли `Title` — САМ ТЕКСТ СОГЛАСИЯ. Пустой
   * объект = сегодняшний вид (14px, цвет наследуется от карточки).
   */
  titleStyle?: React.CSSProperties;
  /**
   * Накладка роли `Secondary` — надписи ОБЕИХ кнопок одной настройкой
   * (однородная пара одного действия; разбор — semanticTextRoles.ts).
   */
  secondaryStyle?: React.CSSProperties;
}

// WEB-683: сама карточка баннера, без позиционирования — переиспользуется
// плеером (внутри fixed-обёртки) и editor-превью (inline). onAccept/onDecline
// опциональны: в превью кнопки ничего не пишут (видимостью управляет панель
// «Показать»). WEB-1330 п.2 (вариант А): раньше обе кнопки звали один и тот
// же onDismiss — теперь развязаны, «Принять»/«Отклонить» пишут РАЗНОЕ решение.
function CookieConsentCard({
  text,
  acceptLabel,
  declineLabel,
  policyUrl,
  position,
  learnMoreLabel,
  titleStyle,
  secondaryStyle,
  onAccept,
  onDecline,
}: CookieConsentBlockProps & {
  onAccept?: () => void;
  onDecline?: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="cookie-consent-banner"
      style={{
        maxWidth: position === 'modal' ? 360 : 480,
        margin: '0 auto',
        background: 'var(--color-bg-surface, #fff)',
        // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
        border: '1px solid var(--color-border-primary, #d9d9d9)',
        borderRadius: 'var(--border-radius-lg, 12px)',
        padding: '16px 20px',
        boxShadow: 'var(--shadow-medium, 0 4px 12px rgba(0,0,0,0.12))',
      }}
    >
      {/* WEB-1043: цвет наследуется от карточки (она резолвит его через
          `--color-text-on-surface`, WEB-966). Пиннить здесь
          `--color-text-primary` нельзя: за порталом он резолвится уже не с
          поверхности воронки, а с `:root` кабинета — на тёмной подложке это
          дало бы почти чёрный текст на тёмном. */}
      <p
        data-testid="cookie-consent-text"
        style={{
          fontSize: 14,
          color: 'inherit',
          margin: '0 0 12px',
          // WEB-1692 юнит 7: накладка роли `Title` спредится ПОСЛЕ прежних
          // строк — при пустом поле объект пуст, и вид старой воронки не
          // меняется ни на байт.
          ...titleStyle,
        }}
      >
        {text}
        {policyUrl && (
          <a
            href={safeHref(policyUrl)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 6, color: 'var(--color-primary, #4094F7)' }}
          >
            {learnMoreLabel}
          </a>
        )}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          data-testid="cookie-consent-decline"
          onClick={onDecline}
          style={{
            padding: '8px 16px',
            border: '1px solid var(--color-border-primary, #d9d9d9)',
            borderRadius: 'var(--border-radius-md, 8px)',
            background: 'transparent',
            // Репорт владельца 01.09 («белый на белом»): у <button> без
            // явного цвета работает НЕ наследование, а цвет из стиля браузера
            // либо цвет поверхности воронки — на тёмной теме это белый текст
            // на светлой карточке. Красим тем же on-surface, которым карточка
            // красит свой текст (WEB-966; переменную переносит портал,
            // WEB-1043); фолбэк тёмный, под светлую подложку карточки.
            color: 'var(--color-text-on-surface, #333)',
            fontSize: 13,
            cursor: 'pointer',
            // WEB-1692 юнит 7: роль `Secondary` — ОБЕ надписи одной настройкой.
            ...secondaryStyle,
          }}
        >
          {declineLabel}
        </button>
        <button
          data-testid="cookie-consent-accept"
          onClick={onAccept}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: 'var(--border-radius-md, 8px)',
            background: 'var(--color-primary, #4094F7)',
            color: 'var(--color-on-primary, #fff)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            // Та же роль `Secondary`: заливка кнопки под настройку не попадает
            // — это семья полей поверхности кнопки (WEB-1651-2).
            ...secondaryStyle,
          }}
        >
          {acceptLabel}
        </button>
      </div>
    </div>
  );
}

function CookieConsentBlock({
  text,
  acceptLabel,
  declineLabel,
  policyUrl,
  position,
  learnMoreLabel,
  titleStyle,
  secondaryStyle,
}: CookieConsentBlockProps): React.ReactElement | null {
  // WEB-683: в editor-превью баннер живёт inline внутри телефона и
  // показывается только по кнопке «Показать» из панели (транзиент
  // cookiePreviewVisible). Раньше: position:fixed вырывался из превью и
  // оверлеил весь редактор, а локальный useState(true) ресетился при каждом
  // ремоунте (переключение экранов) — баннер «всплывал сам».
  const { isEditorPreview, cookiePreviewVisible } = useEditorPreview();
  // WEB-1330 п.2 (вариант А): если решение уже записано (посетитель решил на
  // предыдущем экране/визите — тем же w2a_consent, который читает системный
  // ConsentBanner), баннер не всплывает заново. document может отсутствовать
  // при SSR — тогда прежнее поведение (виден до первого клиентского решения).
  const [visible, setVisible] = useState<boolean>(
    () =>
      typeof document === 'undefined' || readConsentDecision() === undefined,
  );
  // WEB-1330 п.2: страница (PublicQuiz/PublicPaywall) отдаёт свой handleConsent
  // через контекст — вне провайдера безопасный no-op, решение блок всё равно
  // пишет сам ниже (writeConsentDecision), контекст только про мгновенную
  // дозагрузку пикселей без перезагрузки страницы.
  const { onConsentDecision } = useConsentActions();
  // WEB-1043: якорь остаётся на месте блока в потоке — с него снимаются
  // переменные оформления для узла портала (см. usePortalSurfaceVars).
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const portalVars = usePortalSurfaceVars(anchor);

  // WEB-1330 п.2 (вариант А): «Принять»/«Отклонить» перестают быть
  // декорацией — пишут РАЗНОЕ решение через ровно тот же арбитр согласия
  // (euConsent.writeConsentDecision), что читает системный ConsentBanner и
  // загрузчик пикселей (fail-closed, WEB-1258: явный denied уважается
  // всегда). onConsentDecision — best-effort уведомление страницы.
  const handleAccept = (): void => {
    writeConsentDecision('granted');
    setVisible(false);
    onConsentDecision('granted');
  };
  const handleDecline = (): void => {
    writeConsentDecision('denied');
    setVisible(false);
    onConsentDecision('denied');
  };

  if (isEditorPreview) {
    if (!cookiePreviewVisible) {
      return (
        <div
          data-testid="cookie-consent-editor-placeholder"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 'var(--border-radius-md, 8px)',
            border: '1px dashed var(--color-border-primary, #d9d9d9)',
            background: 'var(--color-bg-surface, #fafafa)',
            // Скрин владельца 01.09 (тот же класс, что кнопка «Отклонить»):
            // --color-text-secondary в превью пиннит белым слой фото-вуали
            // экрана — белый текст на светлой карточке плейсхолдера. Красим
            // on-surface (WEB-966), как любой текст на светлой подложке.
            color: 'var(--color-text-on-surface, #8c8c8c)',
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          {pickStrings(authorHintsStrings).cookieBannerPreviewHint}
        </div>
      );
    }
    // Развёрнутый вид: та же карточка, но inline в потоке блока (не fixed).
    return (
      <div style={{ marginBottom: 16 }}>
        <CookieConsentCard
          text={text}
          acceptLabel={acceptLabel}
          declineLabel={declineLabel}
          policyUrl={policyUrl}
          position={position}
          learnMoreLabel={learnMoreLabel}
          titleStyle={titleStyle}
          secondaryStyle={secondaryStyle}
        />
      </div>
    );
  }

  if (!visible) return null;

  const wrapperStyle: React.CSSProperties =
    position === 'modal'
      ? {
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
        }
      : {
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          padding: 16,
        };

  const layer = (
    <div
      data-testid="cookie-consent-layer"
      style={{
        ...wrapperStyle,
        // WEB-1043: портал обрывает наследование от корня поверхности воронки
        // (переменные там инлайновые) — возвращаем их явно.
        ...portalVars,
        fontFamily: 'var(--font-family-primary, Inter, sans-serif)',
      }}
    >
      <CookieConsentCard
        text={text}
        acceptLabel={acceptLabel}
        declineLabel={declineLabel}
        policyUrl={policyUrl}
        position={position}
        learnMoreLabel={learnMoreLabel}
        titleStyle={titleStyle}
        secondaryStyle={secondaryStyle}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />
    </div>
  );

  return (
    <>
      {/* WEB-1043: место блока в потоке. Ничего не рисует и не занимает
          места — служит точкой, с которой снимается каскад оформления. */}
      <span
        ref={setAnchor}
        data-testid="cookie-consent-anchor"
        aria-hidden="true"
        style={{ display: 'none' }}
      />
      {/* WEB-1043 (тот же корень, что у попапа даунселла в WEB-712): корень
          экрана несёт заливку анимации появления (.funnel-screen-in,
          animation-fill-mode: both), а анимированный transform вычисляется в
          матрицу — то есть НЕ в `none`. Такой предок становится содержащим
          блоком для position:fixed, и плашка позиционируется от экрана, а не
          от вьюпорта. Портал в <body> выносит её из-под любого такого
          предка. С 21.10.2026 заливка — backwards, матрица после конца
          анимации не остаётся, но во время анимации появления transform на
          корне действует — портал по-прежнему нужен. */}
      {typeof document !== 'undefined'
        ? createPortal(layer, document.body)
        : layer}
    </>
  );
}

/** Ветка `cookie_consent` фасада — тело `case` слово в слово. */
export function renderCookieConsent(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, richText, hasText, interp } = ctx;
  const ai = aiFields(block);
  // Репорт владельца 01.09: фолбэки — из словаря по языку воронки (тот
  // же путь, что у scratch_card/blur_teaser), а не английский хардкод.
  const cookieStrings = getPublicQuizStrings(varCtx?.system?.locale);
  const text = block.cookieConsentText ?? ai.text;
  // WEB-2923: подписи кнопок — через дверь подстановки (`interp`), как текст
  // баннера (`richText`); у словарного умолчания переменных нет.
  const acceptLabel = interp(
    block.cookieAcceptLabel ?? ai.acceptLabel ?? cookieStrings.consentAccept,
  );
  const declineLabel = interp(
    block.cookieDeclineLabel ?? ai.declineLabel ?? cookieStrings.consentDecline,
  );
  const policyUrl = block.cookiePolicyUrl ?? ai.policyUrl;
  const position = block.cookiePosition ?? ai.position ?? 'bottom';
  // WEB-1692 юнит 7: ДВЕ ДВЕРИ отрисовки баннера — inline-превью
  // редактора и портал на публичной странице, — и обе обязаны получить
  // накладки ролей. Урок юнита 5 («Оффер удержания»): оформление,
  // доведённое до одной двери, на второй молча исчезает.
  const cookieRoles = blockTextRoles(block, 'cookie_consent');
  return (
    <CookieConsentBlock
      text={hasText(text) ? richText(text) : cookieStrings.cookieBlockMessage}
      acceptLabel={acceptLabel}
      declineLabel={declineLabel}
      policyUrl={policyUrl}
      position={position}
      learnMoreLabel={cookieStrings.cookiePolicyLearnMore}
      titleStyle={cookieRoles.title}
      secondaryStyle={cookieRoles.secondary}
    />
  );
}
